'use client';

import { Stack, Theme, Typography } from '@mui/material';
import { ReactElement, ReactNode } from 'react';

export type TConfidenceGuidanceProps = {
  /** Reason-free next-step guidance. */
  children: ReactNode;
  'data-testid'?: string;
};

/**
 * The "what do I do now" box shown beneath an outcome that may need the client to act.
 *
 * It deliberately carries no reason. One message serves every cause, because naming the cause
 * would let the client work out which check produced the outcome.
 */
export default function ConfidenceGuidance({
  children,
  'data-testid': dataTestId,
}: Readonly<TConfidenceGuidanceProps>): ReactElement {
  return (
    <Stack
      data-testid={dataTestId}
      alignItems="flex-start"
      sx={(theme: Theme) => ({
        // A flex column, not the caption's own inline box, is what encloses the tinted background
        // around the whole area rather than wrapping it line by line. It only looked right inside
        // the cards before because a flex parent there blockified the bare caption; here the
        // guidance box supplies its own enclosure regardless of what it is asked to carry.
        backgroundColor: theme.palette.confidence.guidance.fill,
        color: theme.palette.confidence.guidance.text,
        borderRadius: theme.semantic.borderRadius.sm,
        padding: theme.semantic.spacing.sm,
        gap: theme.semantic.spacing.xs,
      })}
    >
      <Typography variant="caption" sx={{ textWrap: 'pretty' }}>
        {children}
      </Typography>
    </Stack>
  );
}
